import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, fileToBase64 } from "../api";
import { todayISO } from "../utils/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

export default function ExpenseDialog({ vehicleId, vehicleLabel, defaultGroup, onSaved, triggerLabel = "Ajouter une dépense", trigger }) {
  const [open, setOpen] = useState(false);
  const [meta, setMeta] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    vehicle_id: vehicleId || "", date: todayISO(), time: "", group: defaultGroup || "Autres", category: "",
    description: "", supplier: "", amount: "", currency: "FCFA", exchange_rate: "",
    payment_method: "Espèces", account_id: "", invoice_ref: "", receipt_number: "",
    payment_ref: "", due_date: "", comment: "", paid_now: true,
  });
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
    if (!vehicleId) api.get("/vehicles").then((r) => setVehicles(r.data));
    setForm((f) => ({ ...f, vehicle_id: vehicleId || "", group: defaultGroup || f.group, category: "" }));
    setReceipt(null);
  }, [open, defaultGroup, vehicleId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Montant invalide");
    if (!vehicleId && !form.vehicle_id) return toast.error("Véhicule requis (ou choisissez « Charge générale »)");
    setSaving(true);
    try {
      const res = await api.post("/expenses", {
        ...form,
        vehicle_id: form.vehicle_id === "general" ? null : form.vehicle_id,
        amount: Number(form.amount),
        exchange_rate: form.exchange_rate ? Number(form.exchange_rate) : null,
        account_id: form.account_id || null,
        due_date: form.due_date || null,
        time: form.time || null,
        receipt_data: receipt,
      });
      if (res.data.status === "En attente") {
        toast.warning("Dépense enregistrée — EN ATTENTE DE VALIDATION");
      } else {
        toast.success("Dépense enregistrée — coût de revient mis à jour");
      }
      setOpen(false);
      setReceipt(null);
      onSaved?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const groups = meta?.expense_groups || {};
  const fcfaPreview = form.currency !== "FCFA" && form.amount
    ? Number(form.amount) * Number(form.exchange_rate || meta?.rates?.[form.currency] || 1)
    : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm" data-testid={`add-expense-${(defaultGroup || "general").toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <Plus className="mr-1 h-4 w-4" /> {triggerLabel}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nouvelle dépense {vehicleLabel ? `— ${vehicleLabel}` : ""}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!vehicleId && (
            <div className="col-span-2">
              <Label>Véhicule concerné *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                <SelectTrigger data-testid="expense-vehicle-select"><SelectValue placeholder="Choisir un véhicule…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">— Charge générale entreprise (aucun véhicule) —</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="expense-date-input" /></div>
          <div><Label>Heure</Label><Input type="time" value={form.time} onChange={(e) => set("time", e.target.value)} /></div>
          <div>
            <Label>Catégorie</Label>
            <Select value={form.group} onValueChange={(v) => setForm((f) => ({ ...f, group: v, category: "" }))}>
              <SelectTrigger data-testid="expense-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(groups).map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Sous-catégorie</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
              <SelectContent>{(groups[form.group] || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="expense-description-input" /></div>
          <div><Label>Fournisseur</Label><Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="expense-supplier-input" /></div>
          <div>
            <Label>Montant *</Label>
            <Input type="number" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="expense-amount-input" />
            {fcfaPreview != null && <p className="mt-1 text-xs text-cyan-600 font-mono-num">= {Math.round(fcfaPreview).toLocaleString("fr-FR")} FCFA</p>}
          </div>
          <div>
            <Label>Devise</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.currencies || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.currency !== "FCFA" && (
            <div><Label>Taux de change (défaut {meta?.rates?.[form.currency]})</Label><Input type="number" value={form.exchange_rate} onChange={(e) => set("exchange_rate", e.target.value)} data-testid="expense-rate-input" /></div>
          )}
          <div>
            <Label>Mode de paiement</Label>
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger data-testid="expense-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.payment_methods || []).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Compte utilisé</Label>
            <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="expense-account-select"><SelectValue placeholder="Aucun impact trésorerie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.account_id && (
            <div>
              <Label>Règlement</Label>
              <Select value={form.paid_now ? "now" : "later"} onValueChange={(v) => set("paid_now", v === "now")}>
                <SelectTrigger data-testid="expense-paidnow-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="now">Payée immédiatement</SelectItem>
                  <SelectItem value="later">Payée plus tard</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>N° de facture</Label><Input value={form.invoice_ref} onChange={(e) => set("invoice_ref", e.target.value)} data-testid="expense-invoice-input" /></div>
          <div><Label>N° de reçu</Label><Input value={form.receipt_number} onChange={(e) => set("receipt_number", e.target.value)} /></div>
          <div><Label>Référence paiement</Label><Input value={form.payment_ref} onChange={(e) => set("payment_ref", e.target.value)} /></div>
          <div><Label>Échéance (si paiement différé)</Label><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="expense-due-date" /></div>
          <div className="col-span-2"><Label>Commentaire</Label><Input value={form.comment} onChange={(e) => set("comment", e.target.value)} /></div>
          <div className="col-span-2">
            <Label>Justificatif (photo, facture, PDF, capture de paiement)</Label>
            <Input type="file" accept="image/*,.pdf" data-testid="expense-receipt-input"
              onChange={async (e) => setReceipt(e.target.files?.[0] ? await fileToBase64(e.target.files[0]) : null)} />
            <p className="mt-1 text-xs text-slate-400">Sans justificatif, la dépense peut rester « En attente » selon les paramètres.</p>
          </div>
        </div>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
          Numérotation automatique DEP-000001… · Au-delà de votre seuil d'autorisation, la dépense part en validation (directeur/admin).
        </p>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="expense-submit-button">
            {saving ? "Enregistrement…" : "Enregistrer la dépense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
